/**
 * Mock Configuration
 *
 * Central configuration file to enable/disable mocks across the application.
 *
 * ⚠️ RECOMMENDED: Use the `useMocks()` hook in React components to control mocks via feature flags.
 * This file provides a static fallback configuration for non-React contexts.
 *
 * @see {@link useMocks} - Hook to control mocks via feature flags
 *
 * @example
 * ```tsx
 * // In React components (RECOMMENDED):
 * import { useMocks } from '@/hooks/useMocks';
 *
 * function MyComponent() {
 *   const { shouldUseMocks } = useMocks();
 *   const useSonarprintMocks = shouldUseMocks('sonarprint-api');
 *   // ...
 * }
 * ```
 *
 * @example
 * ```ts
 * // In non-React contexts (fallback):
 * import { shouldUseMocks } from '@/config/mockConfig';
 *
 * if (shouldUseMocks('sonarprint-api')) {
 *   // Use mock data
 * }
 * ```
 */

export type Services =
  | 'sonarprint-api'
  | 'sonarprint-reports'
  | 'sonarprint-users'
  | 'batch'
  | 'credentials'
  | 'deviceBlocks'
  | 'tagsManagement';

type MockConfig = {
  readonly USE_MOCKS: boolean;
  readonly services: {
    readonly [key in Services]?: boolean;
  };
};

export const MOCK_CONFIG: MockConfig = {
  /**
   * Global flag to enable/disable all mocks.
   * Set to `true` to use mock data, `false` to use real API calls.
   */
  USE_MOCKS: false,

  /**
   * Service-specific mock flags.
   * These override the global USE_MOCKS flag if set.
   */
  services: {
    'sonarprint-api': false,
    'sonarprint-reports': false,
    'sonarprint-users': false,
    batch: false, // Enable mocks until Batch backend contract is defined
    credentials: false, // Enable mocks until Credentials backend is ready
    deviceBlocks: false, // Real endpoints. NOTE: the set call blocks/unblocks live production devices.
    tagsManagement: false, // Real endpoints. NOTE: the set call assigns tags to live production devices.
  },
} as const;

/**
 * Checks if mocks should be used for a specific service.
 *
 * @param serviceName - The name of the service
 * @returns true if mocks should be used, false otherwise
 */
export const shouldUseMocks = (serviceName: keyof typeof MOCK_CONFIG.services): boolean => {
  const serviceConfig = MOCK_CONFIG.services[serviceName];

  // If service has a specific config, use it
  if (serviceConfig !== undefined) {
    return serviceConfig;
  }

  // Otherwise, use the global config
  return MOCK_CONFIG.USE_MOCKS;
};
